import type { IconProps } from '@crehana/compass.icons.outline/types';
import { Edit2 } from '@crehana/compass.icons.outline/v2/edit2';
import { ExternalLink } from '@crehana/compass.icons.outline/v2/external-link';
import { Globe } from '@crehana/compass.icons.outline/v2/globe';
import { Pin } from '@crehana/compass.icons.outline/v2/pin';
import { Shield } from '@crehana/compass.icons.outline/v2/shield';
import { Smartphone } from '@crehana/compass.icons.outline/v2/smartphone';
import { Avatar } from '@crehana/compass.ui/v3/avatar';
import { Button } from '@crehana/compass.ui/v3/button';
import { Dialog } from '@crehana/compass.ui/v3/dialog';
import { IconButton } from '@crehana/compass.ui/v3/icon-button';
import { createColumns, DataTable } from '@crehana/compass.ui/v3/table';
import { Tag } from '@crehana/compass.ui/v3/tag';
import type { FC } from 'react';

import { useNavigate } from '@/shared/modern-router';

import { useTranslation } from '../i18n';
import type {
  TClockEventStatus,
  TClockEventType,
  TClockType,
  TCollaboratorRow,
  TDailyDetail,
  TDayRecord,
} from '../types';

type TDayRecordsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collaborator: TCollaboratorRow;
  day: TDailyDetail;
};

const EVENT_TYPE_TAG_VARIANT: Record<
  TClockEventType,
  'success' | 'info' | 'warning' | 'pending'
> = {
  entry: 'success',
  break_start: 'info',
  break_end: 'warning',
  exit: 'pending',
};

const STATUS_COLOR: Record<TClockEventStatus, { dot: string; text: string }> = {
  on_time: {
    dot: 'tw-cds-bg-success',
    text: 'tw-cds-text-success',
  },
  acceptable: {
    dot: 'tw-cds-bg-progress',
    text: 'tw-cds-text-progress',
  },
  late_or_absent: {
    dot: 'tw-cds-bg-alert',
    text: 'tw-cds-text-alert',
  },
};

const CLOCK_TYPE_ICON: Record<TClockType, FC<IconProps>> = {
  web: Globe,
  qr_tablet: Smartphone,
  app_mobile: Smartphone,
  biometric: Shield,
};

function formatDialogDate(isoDate: string): string {
  const date = new Date(`${isoDate}T12:00:00`);

  return date.toLocaleDateString('es', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export const DayRecordsDialog = ({
  open,
  onOpenChange,
  collaborator,
  day,
}: TDayRecordsDialogProps) => {
  const { t } = useTranslation();
  const { createUrl } = useNavigate();

  const eventTypeLabel: Record<TClockEventType, string> = {
    entry: t('RECORDS_EVENT_ENTRY'),
    break_start: t('RECORDS_EVENT_BREAK_START'),
    break_end: t('RECORDS_EVENT_BREAK_END'),
    exit: t('RECORDS_EVENT_EXIT'),
  };

  const statusLabel: Record<TClockEventStatus, string> = {
    on_time: t('RECORDS_STATUS_ON_TIME'),
    acceptable: t('RECORDS_STATUS_ACCEPTABLE'),
    late_or_absent: t('RECORDS_STATUS_LATE_OR_ABSENT'),
  };

  const clockTypeLabel: Record<TClockType, string> = {
    web: t('RECORDS_CLOCK_WEB'),
    qr_tablet: t('RECORDS_CLOCK_QR_TABLET'),
    app_mobile: t('RECORDS_CLOCK_APP_MOBILE'),
    biometric: t('RECORDS_CLOCK_BIOMETRIC'),
  };

  const columns = createColumns<TDayRecord>(({ column, cells }) => [
    column.accessor('actualTime', {
      header: t('RECORDS_COL_TIME'),
      cell: cells.text(row => ({
        title: row.actualTime,
        subtitle: t('RECORDS_ESTIMATED', {
          interpolation: { time: row.estimatedTime },
        }),
        bold: true,
      })),
    }),
    column.accessor('eventType', {
      header: t('RECORDS_COL_EVENT'),
      cell: info => (
        <Tag variant={EVENT_TYPE_TAG_VARIANT[info.row.original.eventType]}>
          {eventTypeLabel[info.row.original.eventType]}
        </Tag>
      ),
    }),
    column.accessor('status', {
      header: t('RECORDS_COL_STATUS'),
      cell: info => {
        const { status } = info.row.original;
        const colors = STATUS_COLOR[status];

        return (
          <span
            className={`tw-flex tw-items-center tw-gap-0.8 tw-cds-font-body-01 ${colors.text}`}
          >
            <span
              className={`tw-w-1 tw-h-1 tw-rounded-full tw-shrink-0 ${colors.dot}`}
            />
            {statusLabel[status]}
          </span>
        );
      },
    }),
    column.accessor('location', {
      header: t('RECORDS_COL_LOCATION'),
      cell: info => (
        <span className="tw-flex tw-items-center tw-gap-0.4 tw-cds-font-body-01 tw-cds-text-primary">
          <Pin className="tw-w-1.6 tw-h-1.6 tw-cds-text-subtle tw-shrink-0" />
          {info.row.original.location}
        </span>
      ),
    }),
    column.accessor('clockType', {
      header: t('RECORDS_COL_CLOCK_TYPE'),
      cell: info => {
        const ClockIcon = CLOCK_TYPE_ICON[info.row.original.clockType];

        return (
          <span className="tw-flex tw-items-center tw-gap-0.4 tw-cds-font-body-01 tw-cds-text-primary">
            <ClockIcon className="tw-w-1.6 tw-h-1.6 tw-cds-text-subtle tw-shrink-0" />
            {clockTypeLabel[info.row.original.clockType]}
          </span>
        );
      },
    }),
    column.display({
      id: 'edit',
      cell: () => (
        <IconButton
          variant="secondary"
          size="xs"
          icon={Edit2}
          aria-label={t('EMPLOYEE_DIALOG_DETAIL_RECORDS_EDIT_RECORD')}
        />
      ),
      size: 48,
    }),
  ]);

  const handleViewAllRecords = () => {
    const url = createUrl({
      path: '/org/[slug]/attendance/admin/records/',
      searchParams: { email: encodeURIComponent(collaborator.email) },
    });

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="lg">
        <Dialog.CloseButton />
        <Dialog.Header>
          <Dialog.Title>{t('RECORDS_DIALOG_TITLE')}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Description srOnly>
          {t('RECORDS_DIALOG_TITLE')}
        </Dialog.Description>
        <Dialog.Body>
          <div className="tw-flex tw-items-center tw-gap-1.2 tw-cds-rounded-s tw-p-1.2 tw-mb-1.6 tw-border tw-border-solid tw-cds-border-color-default tw-cds-bg-layer-02">
            <Avatar size="sm" userName={collaborator.name} />
            <div className="tw-flex-1 tw-flex tw-flex-col">
              <span className="tw-cds-font-subtitle-02">
                {collaborator.name}
              </span>
              <span className="tw-cds-font-body-01 tw-cds-text-subtle">
                {collaborator.role} &middot; {formatDialogDate(day.date)}
              </span>
            </div>

            <span className="tw-flex tw-items-center tw-gap-0.8 tw-cds-font-body-01">
              <span
                className="tw-w-1 tw-h-1 tw-rounded-full tw-shrink-0"
                style={{ backgroundColor: collaborator.shiftColor }}
              />
              {collaborator.shiftName}
            </span>
          </div>

          <DataTable
            columns={columns}
            data={day.records}
            rowCount={day.records.length}
            density="compact"
          />
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Close
            render={
              <Button variant="tertiary" className="md:tw-mr-auto">
                {t('RECORDS_DIALOG_CLOSE')}
              </Button>
            }
          />
          <Button
            variant="primary"
            startIcon={ExternalLink}
            onClick={handleViewAllRecords}
          >
            {t('RECORDS_DIALOG_VIEW_ALL')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
